import api from 'api';
import { AxiosResponse } from 'axios';
import { BoxWrapper, Button, ButtonExpand, Prompt } from 'components/Base';
import BulkImport from 'components/BulkImport/BulkImport';
import Fields from 'components/Fields';
import LoaderSpinner from 'components/LoaderSpinner/LoaderSpinner';
import TextInput from 'components/TextInput/TextInput';
import React, { useEffect, useState } from 'react';
import { MetaType } from 'store/modules/Meta/types';
import { errHelper, Notify, pluralise } from 'utils';
import { investmentType } from '../types';
import Investment from './Investment';
import { isEqual } from 'lodash';
import { useDebouncedCallback } from 'use-debounce';
import Icons from 'components/Icons';
import { Typography, CircularProgress } from '@material-ui/core';
import ModelPortfolioSelector from './ModelPortfolioSelector';
import Store from 'store/store';
import { connect } from 'react-redux';
import { compose } from 'redux-starter-kit';

type CipThresholdProps = {
  cip: MetaType;
  index: number;
  onCipThresholdSave: Function;
  onDeleteCipThreshold: Function;
  cashFund: any;
  isFirmAdmin: boolean;
};

type MpMeta = {
  isSaved: boolean;
  isSelected: boolean;
  name: string;
  value: string;
};

export enum chargeTypeEnum {
  MONETARY = 1,
  PERCENTAGE = 2
}

const { selectors } = Store;

const CipThreshold: React.FC<CipThresholdProps> = ({
  cip,
  index,
  onCipThresholdSave,
  onDeleteCipThreshold,
  cashFund,
  isFirmAdmin
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [investments, setInvestments] = useState([] as any[]);
  const [funds, setFunds] = useState([] as any[]);
  const [modelPortfolios, setModelPortfolios] = useState([] as any[]);
  const [customModelPortfolios, setCustomModelPortfolios] = useState(
    [] as any[]
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isModelPortfolioSelectorOpen, setIsModelPortfolioSelectorOpen] =
    useState(false);
  const [modelPortfolioOption, setModelPortfolioOption] = useState('select');
  const [addingModelPortfolioSelection, setAddingModelPortfolioSelection] =
    useState(false);

  const saveCipThreshold = useDebouncedCallback(name => {
    onCipThresholdSave({ ...cip, name }, index);
  }, 1000);

  const loadCipOptions = () => {
    setIsLoading(true);
    api
      .getCipRelations({ cip_thresholds: [Number(cip.value)] })
      .then((response: AxiosResponse) => {
        if (response.data[cip.value]) {
          const investmentData = [
            ...response.data[cip.value].funds,
            ...response.data[cip.value].model_portfolios
          ];
          setInvestments(investmentData);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const deleteInvestment = (investment: investmentType) => {
    setIsDeleting(true);
    const toDelete = {
      type: investment.mp_id ? 'mp' : 'f',
      id: investment.id,
      cip_threshold_id: Number(cip.value)
    };
    api
      .deleteCipRelations(toDelete)
      .then(() => {
        const index = investments.findIndex(listedInvestment =>
          isEqual(listedInvestment, investment)
        );
        const listedInvestments = [...investments];
        listedInvestments.splice(index, 1);
        setInvestments(listedInvestments);
        Notify.success(
          `You have sucessfully deleted ${investment.name} from ${cip.name}`
        );
        setIsDeleting(false);
      })
      .catch(err => {
        errHelper(err);
        setIsDeleting(false);
      });
  };

  const addModelPortfolioSelection = (data: any) => {
    const investmentsToUpdate = investments;
    const updatedInvestments = investmentsToUpdate.reduce(
      (investmentsArr, investment) => {
        if (investment.id === Number(data.value)) {
          investment.mp_id = data.mp_id;
          investment.last_updated = data.last_updated;
          investment.name = data.name;
          investmentsArr.push(investment);
        } else investmentsArr.push(investment);
        return investmentsArr;
      },
      []
    );
    setInvestments([...updatedInvestments]);
  };

  const saveMpInvestments = (mpInvestmentsToSave: MpMeta[] | MpMeta) => {
    const isMultiple = Array.isArray(mpInvestmentsToSave);

    if (
      !isMultiple &&
      investments
        .map((investment: any) => investment.id)
        .includes(Number(mpInvestmentsToSave.value))
    ) {
      addModelPortfolioSelection(mpInvestmentsToSave);
      return;
    }

    const toSaveRequests = isMultiple
      ? (mpInvestmentsToSave as MpMeta[]).map(its => {
          return {
            type: 'mp',
            cip_threshold_id: Number(cip.value),
            model_portfolio_selection_id: its.value
          };
        })
      : [
          {
            type: 'mp',
            cip_threshold_id: Number(cip.value),
            model_portfolio_selection_id: (mpInvestmentsToSave as MpMeta).value
          }
        ];
    setAddingModelPortfolioSelection(true);
    api
      .updateCipRelations(toSaveRequests)
      .then(response => {
        const investmentData = investments;
        investmentData.push(...response.data);
        setInvestments([...investmentData]);
        Notify.success(
          `You have sucessfully added ${
            isMultiple ? (mpInvestmentsToSave as MpMeta[]).length : '1'
          } model portfolios to ${cip.name}`
        );
      })
      .catch(err => errHelper(err))
      .finally(() => {
        setAddingModelPortfolioSelection(false);
        if (modelPortfolioOption === 'select') {
          setIsModelPortfolioSelectorOpen(false);
        }
      });
  };

  const saveFundInvestments = (fundInvestmentsToSave: any) => {
    const toSaveRequests = fundInvestmentsToSave.map((its: any) => {
      return {
        type: 'f',
        cip_threshold_id: Number(cip.value),
        fund_id: its.fund.id
      };
    });
    api
      .updateCipRelations(toSaveRequests)
      .then(() => {
        const investmentData = investments;
        const newFunds = fundInvestmentsToSave.map((fi: any) => {
          return {
            id: fi.fund.id,
            citi_code: fi.fund.citi_code,
            name: fi.fund.name
          };
        });
        setInvestments([...investmentData, ...newFunds]);
        Notify.success(
          `You have sucessfully added ${fundInvestmentsToSave.length} funds to ${cip.name}`
        );
      })
      .catch(err => errHelper(err));
  };

  const saveFundInvestment = (fund: any) => {
    const toSave = {
      type: 'f',
      cip_threshold_id: Number(cip.value),
      fund_id: fund.id
    };
    api
      .updateCipRelations([toSave])
      .then(() => {
        const investmentData = investments;
        const newFund = {
          id: fund.id,
          citi_code: fund.citi_code,
          name: fund.name
        };
        setInvestments([...investmentData, newFund]);
        Notify.success(
          `You have sucessfully added ${fund.name} funds to ${cip.name}`
        );
      })
      .catch(err => errHelper(err));
  };

  const deleteCipThreshold = () => {
    api
      .deleteCipThreshold({ id: Number(cip.value) })
      .then(() => {
        onDeleteCipThreshold(index);
        Notify.success(`You have sucessfully deleted ${cip.name}`);
      })
      .catch(err => errHelper(err));
  };

  useEffect(() => {
    loadCipOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFunds(investments.filter(investment => !investment.mp_id));
    setModelPortfolios(
      investments.filter(
        investment => investment.mp_id && investment.cip_category
      )
    );
    setCustomModelPortfolios(
      investments.filter(
        investment => investment.mp_id && !investment.cip_category
      )
    );
  }, [investments]);

  const openModelPortfolioModal = (type: string) => {
    setModelPortfolioOption(type);
    setIsModelPortfolioSelectorOpen(true);
  };

  const hasCashFund = funds.some((fund: any) => {
    return fund.id === cashFund.id;
  });

  const toggleCashFund = (fund: any) => {
    if (hasCashFund) {
      return deleteInvestment(fund);
    }
    return saveFundInvestment(fund);
  };

  return (
    <BoxWrapper style={{ marginBottom: '2rem' }}>
      <div className="cipThresholdBar">
        <div style={{ width: '300px' }}>
          {isFirmAdmin ? (
            <TextInput
              label={`Investment Option ${index + 1}`}
              onChange={saveCipThreshold}
              value={cip.name}
            />
          ) : (
            <Typography
              variant="h3"
              style={{
                fontSize: '1.1rem',
                lineHeight: '1.3rem',
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
            >
              {cip.name}
            </Typography>
          )}
        </div>
        <div>
          {isLoading ? (
            <CircularProgress color="inherit" size={20} />
          ) : (
            <div style={{ fontSize: '12px', lineHeight: '0.8rem' }}>
              <div>
                <strong>{funds.length}</strong>{' '}
                {pluralise(funds.length, 'Fund')}
              </div>
              <div>
                <strong>{modelPortfolios.length}</strong>{' '}
                {pluralise(modelPortfolios.length, 'Model Portfolio')}
              </div>
              <div>
                <strong>{customModelPortfolios.length}</strong>{' '}
                {pluralise(customModelPortfolios.length, 'Custom Portfolio')}
              </div>
            </div>
          )}
        </div>
        <div className="cipThresholdActions">
          {!!cip.value && (
            <>
              <ButtonExpand
                style={{
                  height: '42px',
                  marginRight: '1rem'
                }}
                isOpen={isOpen}
                onClick={() => setIsOpen(!isOpen)}
              >
                Expand
              </ButtonExpand>
              {isFirmAdmin && (
                <div
                  className="cipThresholdDelete"
                  onClick={() => setIsDeleteModalOpen(true)}
                >
                  <Icons.DeleteIcon />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <div
        className="investmentListContainer"
        style={
          isOpen
            ? {
                height: `${
                  investments.length * (isFirmAdmin ? 68 : 52) +
                  (isLoading ? 125 : isFirmAdmin ? 410 : 156)
                }px`,
                borderTop: '1px solid #eaeaea',
                paddingBottom: '1rem'
              }
            : { height: 0 }
        }
      >
        {isLoading ? (
          <LoaderSpinner isLoading={isLoading} />
        ) : (
          <>
            {funds.length ? (
              <>
                <div
                  style={{ padding: '1rem 2rem 1rem 2rem', width: '100%' }}
                  className="investmentTitle"
                >
                  <span style={{ fontWeight: 'bold', width: '90%' }}>
                    Funds
                  </span>
                  <span style={{ fontWeight: 'bold', width: '10%' }}></span>
                </div>
                {funds.map(fund => {
                  return (
                    <Investment
                      key={fund.name}
                      investment={fund}
                      deleteInvestment={deleteInvestment}
                      isDeleting={isDeleting}
                      type="fund"
                      isFirmAdmin={isFirmAdmin}
                    />
                  );
                })}
              </>
            ) : (
              <div className="emptyState">
                No Funds have been added to this Investment Option
              </div>
            )}
            {isFirmAdmin && (
              <div className="investmentButtonBar">
                <Typography
                  style={{
                    paddingLeft: '2rem',
                    fontSize: '1rem',
                    lineHeight: '1.3rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap'
                  }}
                  className="investmentGroupTitle"
                  variant="h3"
                >
                  Add Fund:
                </Typography>
                <div style={{ width: '550px', paddingLeft: '2rem' }}>
                  <Fields.Search onSelect={val => saveFundInvestment(val)} />
                </div>
                <Button
                  type="submit"
                  onClick={() => setIsBulkImportOpen(true)}
                  style={{ marginLeft: '2rem' }}
                >
                  Bulk Import
                </Button>
                <div style={{ marginLeft: '1.5rem' }}>
                  <Fields.CheckboxCustom
                    isChecked={hasCashFund}
                    label="Pound sterling"
                    onChange={() => toggleCashFund(cashFund)}
                  />
                </div>
                <BulkImport
                  openBulkImport={isBulkImportOpen}
                  setOpenBulkImport={setIsBulkImportOpen}
                  importToPolicy={(imports: any) =>
                    saveFundInvestments(imports)
                  }
                />
              </div>
            )}
            {modelPortfolios.length ? (
              <>
                <div
                  style={{ padding: '1rem 2rem 1rem 2rem', width: '100%' }}
                  className="investmentTitle"
                >
                  <span
                    className="flexWrap"
                    style={{ fontWeight: 'bold', width: '60%' }}
                  >
                    <div>Model Portfolios</div>
                  </span>
                  <span style={{ fontWeight: 'bold', width: '30%' }}>
                    MPS Charge
                  </span>
                  <span style={{ fontWeight: 'bold', width: '10%' }}></span>
                </div>
                {modelPortfolios.map(modelPortfolio => {
                  return (
                    <Investment
                      key={modelPortfolio.name}
                      investment={modelPortfolio}
                      deleteInvestment={deleteInvestment}
                      isDeleting={isDeleting}
                      type="model portfolio"
                      isFirmAdmin={isFirmAdmin}
                    />
                  );
                })}
              </>
            ) : (
              <div className="emptyState flexWrap">
                <div>
                  No Model Portfolios have been added to this Investment Option
                </div>
              </div>
            )}
            {isFirmAdmin && (
              <div className="investmentButtonBar">
                <Button
                  type="submit"
                  onClick={() => {
                    openModelPortfolioModal('select');
                  }}
                  style={{ marginLeft: '2rem' }}
                >
                  Add Model Portfolio
                </Button>
              </div>
            )}
            {customModelPortfolios.length ? (
              <>
                <div
                  style={{ padding: '1rem 2rem 1rem 2rem', width: '100%' }}
                  className="investmentTitle"
                >
                  <span
                    className="flexWrap"
                    style={{ fontWeight: 'bold', width: '60%' }}
                  >
                    <div>Custom Model Portfolios</div>
                  </span>
                  <span
                    style={{
                      fontWeight: 'bold',
                      width: isFirmAdmin ? '20%' : '30%'
                    }}
                  >
                    MPS Charge
                  </span>
                  {isFirmAdmin && (
                    <span style={{ fontWeight: 'bold', width: '10%' }}>
                      Edit
                    </span>
                  )}
                  <span style={{ fontWeight: 'bold', width: '10%' }}></span>
                </div>
                {customModelPortfolios.map(customModelPortfolio => {
                  return (
                    <Investment
                      key={customModelPortfolio.name}
                      investment={customModelPortfolio}
                      deleteInvestment={deleteInvestment}
                      isDeleting={isDeleting}
                      type="custom model portfolio"
                      investments={investments}
                      addModelPortfolioSelection={saveMpInvestments}
                      isFirmAdmin={isFirmAdmin}
                    />
                  );
                })}
              </>
            ) : (
              <div className="emptyState flexWrap">
                <div>
                  No Custom Portfolios have been added to this Investment Option
                </div>
              </div>
            )}
            {isFirmAdmin && (
              <div className="investmentButtonBar noBorder">
                <Button
                  type="submit"
                  onClick={() => {
                    openModelPortfolioModal('create');
                  }}
                  style={{ marginLeft: '2rem' }}
                >
                  Add Custom Model Portfolio
                </Button>
              </div>
            )}
          </>
        )}
        <div style={{ width: '100%', height: 'max-content' }}>
          <ModelPortfolioSelector
            isOpen={isModelPortfolioSelectorOpen}
            closeModelFunc={() => setIsModelPortfolioSelectorOpen(false)}
            addModelPortfolioSelection={saveMpInvestments}
            addingModelPortfolioSelection={addingModelPortfolioSelection}
            investments={investments}
            option={modelPortfolioOption}
          />
        </div>
      </div>
      <Prompt
        isOpen={isDeleteModalOpen}
        title={`Are you sure you want to delete this Investment Option?`}
        confirmAction={deleteCipThreshold}
        isProcessing={isDeleting}
        cancelAction={() => setIsDeleteModalOpen(false)}
      />
    </BoxWrapper>
  );
};

export default compose(
  connect((state: any) => ({
    cashFund: selectors.metaSelectors.cashFund(state),
    isFirmAdmin: selectors.advisorSelectors.isFirmAdmin(state)
  }))
)(CipThreshold);
